import {
  EMPTY_TOKEN,
  useBalanceHub,
  useProviderHub,
  useTokenInfoHub,
} from '@aries/aptos-defi/common';
import { getLPTokenAddress } from '@aries/aptos-defi/common/aries-sdk';
import { LendingAppDefine } from '@aries/lending-fe-hooks/adapter';
import { createGlobalStore } from '@aries/shared/deps';
import { formatQuoteValue } from '@aries/shared/utils';
import { formatPercentage } from '@aries/shared/utils/quote-value';
import Big from 'big.js';
import { compact, isEmpty, isNil, noop } from 'lodash';
import { useMemo } from 'react';
import {
  APTOS_COIN_ADDRESS,
  MAINNET_ABTC_COIN,
  MAINNET_MERKLE_LP,
  MAINNET_SUSDE_WCOIN,
  MAINNET_USDC_WCOIN,
  MAINNET_WBTC_WCOIN,
  MAINNET_XBTC_WCOIN,
  ZUSDC_ADDRESS,
  ZUSDT_ADDRESS,
} from '../config';
import { useWallet } from '../wallet';
import { claimRewards, getReservesActions } from './actions';
import { useUserBorrowRebate } from './borrow-rebate/rebate-stats';
import { useFaucet, useProfileHub, useReserves } from './data';
import { useMklpApr } from './data/mklp-apr';
import { exitEmode } from './data/profiles/actions';
import { useUSDeStakingYield } from './data/usde-staking-yields';
import { useUnhealthyProfiles } from './liquidate';
import { useOkxEarn } from './okx-earns';
/**
 * Lending app impl on aptos blockchain
 */

const deprecatedReserveIds = [
  ZUSDT_ADDRESS,
  ZUSDC_ADDRESS,
  '0x5e156f1207d0ebfa19a9eeff00d62a282278fb8719f4fab3a586a0a2c0fffbea::coin::T',
  '0xdd89c0e695df0692205912fb69fc290418bed0dbe6e4573d744a6d5e6bab6c13::coin::T',
  '0x84d7aeef42d38a5ffc3ccef853e1b82e4958659d16a7de736a29c55fbbeb0114::staked_aptos_coin::StakedAptosCoin',
  '0xcfea864b32833f157f042618bd845145256b1bf4c0da34a7013b76e42daa53cc::usdy::USDY',
  // CAKE
  '0x159df6b7689437016108a019fd5bef736bac692b6d4a1f10c941f6fbb9a74ca6::oft::CakeOFT',
  // zWBTC (LayerZero)
  '0xf22bede237a07e121b56d91a491eb7bcdfd1f5907926a9e58338f964a01b17fa::asset::WBTC',
  // WETH (Wormhole)
  '0xcc8a89c8dce9693d354449f1f73e60e14e347417854f029db5bc8e7454008abb::coin::T',
  MAINNET_SUSDE_WCOIN,
  MAINNET_ABTC_COIN,
  MAINNET_WBTC_WCOIN,
  MAINNET_XBTC_WCOIN,
];

// Deprecated reserves hidden from the markets/Assets list entirely, so no new
// deposits/borrows can be opened. Most have been wound down on-chain (available
// liquidity ~0, verified via the reserve stats table); the Wormhole-wrapped
// assets are being phased out and may still hold liquidity. They remain in the
// reserve config so existing depositors/borrowers can still repay/withdraw via
// the Dashboard.
const hiddenReserveIds = [
  // USDY (Ondo)
  '0xcfea864b32833f157f042618bd845145256b1bf4c0da34a7013b76e42daa53cc::usdy::USDY',
  // sUSDe (Ethena, wrapped)
  MAINNET_SUSDE_WCOIN,
  // aBTC
  MAINNET_ABTC_COIN,
  // WBTC (native wrapped)
  MAINNET_WBTC_WCOIN,
  // zWBTC (LayerZero)
  '0xf22bede237a07e121b56d91a491eb7bcdfd1f5907926a9e58338f964a01b17fa::asset::WBTC',
  // CAKE
  '0x159df6b7689437016108a019fd5bef736bac692b6d4a1f10c941f6fbb9a74ca6::oft::CakeOFT',
  // tAPT (Tortuga staked APT)
  '0x84d7aeef42d38a5ffc3ccef853e1b82e4958659d16a7de736a29c55fbbeb0114::staked_aptos_coin::StakedAptosCoin',
  // USDC (Wormhole) — being phased out
  '0x5e156f1207d0ebfa19a9eeff00d62a282278fb8719f4fab3a586a0a2c0fffbea::coin::T',
  // WETH (Wormhole) — being phased out
  '0xcc8a89c8dce9693d354449f1f73e60e14e347417854f029db5bc8e7454008abb::coin::T',
  // SOL (Wormhole) — being phased out
  '0xdd89c0e695df0692205912fb69fc290418bed0dbe6e4573d744a6d5e6bab6c13::coin::T',
];

const useAptosLendingAppImpl: () => LendingAppDefine = () => {
  const { env } = useProviderHub();
  const { balanceMap } = useBalanceHub();
  const { tokenMap } = useTokenInfoHub();
  const walletCtx = useWallet();
  const { reserves: reserveMetas, loading } = useReserves();
  const {
    initMainProfile,
    changeProfile,
    addProfile,
    currentProfile,
    currentProfileKey,
    profileList,
    profileLoading,
  } = useProfileHub();

  const { faucets } = useFaucet();
  const { usdeStakingYield } = useUSDeStakingYield();

  const { okxUsdcEarnApy, okxAptEarnApy } = useOkxEarn();

  const { mklpApr } = useMklpApr();

  const reserves = useMemo(
    () =>
      reserveMetas.map(({ coinAddress, ...reserve }) => {
        const tokenInfo = tokenMap[coinAddress] ?? EMPTY_TOKEN;
        const {
          reserveConfig: {
            borrowLimit,
            depositLimit,
            allowCollateral,
            allowRedeem,
            loanToValue,
          },
          totalBorrowed,
          supplyApyPct,
          borrowApyPct,
          supplyRewardApyPct,
          borrowRewardApyPct,
          totalDeposited,
          marketCap,
          borrowFeePct,
          withdrawFeePct,
          maxInterestRatePct,
          optimalInterestRatePct,
          optimalUtilizationPct,
          flashLoanFeePct,
          borrowFactorPct,
          minInterestRatePct,
          utilizationRatio,
        } = reserve;

        const lpCoinAddress = getLPTokenAddress(coinAddress);

        const rawActions = getReservesActions({
          ...reserve,
          coinAddress,
          unwrappedFaAddress: reserve.rawReserve.unwrappedFaAddress,
        });

        const actions: LendingAppDefine['reserves'][number]['actions'] = {
          withdraw: (amount, isMax) =>
            rawActions.withdraw(tokenInfo.toLamports(amount), isMax),
          repay: (amount, isMax) =>
            rawActions.repay(tokenInfo.toLamports(amount), isMax),
          deposit: amount =>
            rawActions.deposit(tokenInfo.toLamports(amount)),
          borrow: amount =>
            rawActions.borrow(tokenInfo.toLamports(amount)),
        };

        const calcApyProps = (
          baseApyPct: Big,
          rewardApyPct: Big,
          isBorrow: boolean,
        ) => {
          let adjustedRewardApyPct = rewardApyPct;
          let totalApyPct = baseApyPct.add(rewardApyPct);

          if (coinAddress === MAINNET_SUSDE_WCOIN && usdeStakingYield) {
            adjustedRewardApyPct = rewardApyPct.add(Big(usdeStakingYield));
            totalApyPct = baseApyPct.add(adjustedRewardApyPct);
          } else if (coinAddress === MAINNET_MERKLE_LP && mklpApr) {
            adjustedRewardApyPct = rewardApyPct.add(Big(mklpApr));
            totalApyPct = baseApyPct.add(adjustedRewardApyPct);
          } else if (
            coinAddress === MAINNET_USDC_WCOIN &&
            okxUsdcEarnApy &&
            isBorrow
          ) {
            try {
              totalApyPct = Big(okxUsdcEarnApy);
              adjustedRewardApyPct = totalApyPct.minus(baseApyPct);
              // eslint-disable-next-line no-empty
            } catch (error) {}
          } else if (
            coinAddress === APTOS_COIN_ADDRESS &&
            okxAptEarnApy &&
            isBorrow
          ) {
            try {
              totalApyPct = Big(okxAptEarnApy);
              adjustedRewardApyPct = totalApyPct.minus(baseApyPct);
              // eslint-disable-next-line no-empty
            } catch (error) {}
          }

          return {
            baseApy: {
              base: `${baseApyPct.toFixed(2)}%`,
              isPositive: baseApyPct.gte(0),
              num: baseApyPct.toNumber(),
            },
            rewardApy: !adjustedRewardApyPct.eq(0)
              ? {
                  base: formatPercentage(adjustedRewardApyPct),
                  isPositive: adjustedRewardApyPct.gte(0),
                  num: adjustedRewardApyPct.toNumber(),
                }
              : undefined,
            totalApy: {
              base: formatPercentage(totalApyPct),
              isPositive: totalApyPct.gte(0),
              num: totalApyPct.toNumber(),
            },
          };
        };

        const findRewardAssets = (
          farmingType: 'DepositFarming' | 'BorrowFarming',
        ) => {
          const assets = compact(
            reserve.rawFarms
              .filter(
                ({ reserveCoinAddress, rewardCondition }) =>
                  reserveCoinAddress === coinAddress &&
                  rewardCondition === farmingType,
              )
              .map(({ rewardCoinAddress, rewardApy }) => ({
                ...tokenMap[rewardCoinAddress],
                rewardApy: formatPercentage(rewardApy),
                description: 'Reward',
                isNegtive: false,
                link: '',
              })),
          );

          if (coinAddress === MAINNET_SUSDE_WCOIN && usdeStakingYield) {
            assets.push({
              ...tokenMap[MAINNET_SUSDE_WCOIN],
              rewardApy: formatPercentage(usdeStakingYield),
              description: 'Ethena Staking',
              isNegtive: farmingType === 'BorrowFarming',
              link: '',
            });
          } else if (coinAddress === MAINNET_MERKLE_LP && mklpApr) {
            assets.push({
              ...tokenMap[MAINNET_MERKLE_LP],
              rewardApy: formatPercentage(mklpApr),
              description: 'Merkle Trade Liquidity Pool',
              isNegtive: farmingType === 'BorrowFarming',
              link: '',
            });
          } else if (
            coinAddress === MAINNET_USDC_WCOIN &&
            okxUsdcEarnApy &&
            farmingType === 'BorrowFarming'
          ) {
            try {
              assets.push({
                ...tokenMap[APTOS_COIN_ADDRESS],
                rewardApy: formatPercentage(
                  Big(okxUsdcEarnApy)
                    .minus(borrowApyPct)
                    .add(borrowRewardApyPct),
                ),
                link: 'https://web3.okx.com/earn/product/aries-markets-aptos-usdc-33336#source=productlist',
                logoURI: '/okx-logo.png',
                description: 'OKX Borrow Bonus',
                isNegtive: false,
              });
              // eslint-disable-next-line no-empty
            } catch (error) {}
          } else if (
            coinAddress === APTOS_COIN_ADDRESS &&
            okxAptEarnApy &&
            farmingType === 'BorrowFarming'
          ) {
            try {
              assets.push({
                ...tokenMap[APTOS_COIN_ADDRESS],
                rewardApy: formatPercentage(
                  Big(okxAptEarnApy)
                    .minus(borrowApyPct)
                    .add(borrowRewardApyPct),
                ),
                link: 'https://web3.okx.com/earn/product/aries-markets-aptos-apt-40049#source=productlist',
                logoURI: '/okx-logo.png',
                description: 'OKX Borrow Bonus',
                isNegtive: false,
              });
              // eslint-disable-next-line no-empty
            } catch (error) {}
          }

          return assets;
        };

        return {
          coinAddress,
          id: coinAddress,
          rawReserve: reserve,
          meta: {
            loanToValue: `${loanToValue}%`,
            loanToValuePct: loanToValue,
            allowCollateral,
            allowWithdraw: allowRedeem,
            lpCoinAddress,
            coinAddress,
            borrowLimit: tokenInfo.toAmountStr(borrowLimit, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
            borrowLimitNum: tokenInfo.toAmount(borrowLimit).toNumber(),
            depositLimit: tokenInfo.toAmountStr(depositLimit, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
            depositLimitNum: tokenInfo.toAmount(depositLimit).toNumber(),
            borrowFeePct: borrowFeePct.toNumber(),
            withdrawFeePct: withdrawFeePct.toNumber(),
            maxInterestRatePct,
            minInterestRatePct,
            optimalInterestRatePct,
            optimalUtilizationPct,
            flashLoanFeePct,
            supplyRewardApyPct,
            borrowRewardApyPct,
            borrowFactorPct,
            deprecated: deprecatedReserveIds.includes(coinAddress),
            hidden: hiddenReserveIds.includes(coinAddress),
          },
          status: {
            marketCap: tokenInfo.lamportsToBalance(marketCap),
            utilizationRatio: utilizationRatio.mul(100).toNumber(),
          },
          asset: tokenInfo,
          deposit: {
            ...calcApyProps(supplyApyPct, supplyRewardApyPct, false),
            rewardAssets: findRewardAssets('DepositFarming'),
            balance: tokenInfo.lamportsToBalance(totalDeposited),
            reachedDepositLimit: totalDeposited.gte(depositLimit),
          },
          borrow: {
            // Borrow rewards contribute negative borrowing APY
            ...calcApyProps(
              borrowApyPct,
              Big(0).sub(borrowRewardApyPct),
              true,
            ),
            rewardAssets: findRewardAssets('BorrowFarming'),
            balance: tokenInfo.lamportsToBalance(totalBorrowed),
          },
          actions,
        };
      }),

    [
      reserveMetas,
      tokenMap,
      usdeStakingYield,
      mklpApr,
      okxUsdcEarnApy,
      okxAptEarnApy,
    ],
  );

  const reserveWithProfile: LendingAppDefine['reserves'] = useMemo(() => {
    return reserves.map(
      ({ coinAddress, status: reserveStatus, ...reserve }) => {
        const tokenInfo = reserve.asset;

        const walletAmount = tokenInfo.lamportsToBalance(
          balanceMap[coinAddress]?.amount ?? Big(0),
        );

        const depositedAsset =
          currentProfile?.getDepositedLamports(coinAddress) ?? Big(0);

        const borrowedLamports =
          currentProfile?.getBorrowedLamports(coinAddress) ?? Big(0);

        const withdrawableAmount =
          currentProfile?.getWithdrawableAmount(coinAddress) ?? Big(0);

        const borrowableAmount =
          currentProfile?.getBorrowableAmount(coinAddress) ?? Big(0);

        const depositable =
          currentProfile?.getDepositableAmount(
            coinAddress,
            walletAmount.lamports,
          ) ?? Big(0);

        const loanToValuePct =
          currentProfile?.getLoanToValuePct(reserve.rawReserve) ??
          reserve.rawReserve.loanToValuePct;
        const liquidationThreshold =
          currentProfile?.getLiquidationThreshold(reserve.rawReserve) ??
          reserve.rawReserve.liquidationThreshold;
        const liquidationBonusPct =
          currentProfile?.getLiquidationBonusPct(reserve.rawReserve) ??
          reserve.rawReserve.liquidationBonusPct;

        return {
          ...reserve,
          meta: {
            ...reserve.meta,
            loanToValue: `${loanToValuePct}%`,
            loanToValuePct,
            liquidationThreshold: `${liquidationThreshold}%`,
            liquidationBonus: `${liquidationBonusPct}%`,
            reserveInEmode: !isEmpty(
              reserve.rawReserve.rawReserve?.emodeIds ?? [],
            ),
          },
          wallet: walletAmount,
          summary: {
            marketCap: reserveStatus.marketCap,
            utilization: reserveStatus.utilizationRatio,
            ofUser: {
              totalMarginValueUSD: `$${currentProfile?.totalMarginUSD?.toFixed(
                3,
              )}`,
            },
          },
          deposit: {
            ...reserve.deposit,
            ofUser: {
              deposited: tokenInfo.lamportsToBalance(depositedAsset),
              withdrawable: tokenInfo.toAmount(withdrawableAmount),
              depositable: tokenInfo.toBalance(depositable),
            },
          },
          borrow: {
            ...reserve.borrow,
            ofUser: {
              available: tokenInfo.toBalance(borrowableAmount),
              borrowed: tokenInfo.lamportsToBalance(borrowedLamports),
            },
            shouldExitEmode:
              !isNil(currentProfile) &&
              !currentProfile.isReserveBorrowable(reserve.rawReserve),
          },
        };
      },
    );
  }, [reserves, balanceMap, currentProfile]);

  const summary = useMemo(() => {
    let marketSize = Big(0);
    let borrowed = Big(0);

    reserves.forEach(reserve => {
      if (reserve.meta.deprecated) {
        return;
      }
      marketSize = marketSize.add(reserve.deposit.balance.valueUSDNum);
      borrowed = borrowed.add(reserve.borrow.balance.valueUSDNum);
    });

    return {
      marketSize: formatQuoteValue(marketSize.toNumber()),
      borrowed: formatQuoteValue(borrowed.toNumber()),
      lentOut: marketSize.eq(0)
        ? '-'
        : `${borrowed.div(marketSize).mul(100).toFixed(1)}%`,
    };
  }, [reserves]);

  const profiles = profileList.map(
    ({
      address,
      name,
      displayName,
      availableMarginUSD,
      totalDepositedUSD,
      totalLoanUSD,
      networth,
      loanList,
      borrowPower,
      depositList,
      riskFactorPct,
      totalApyPct,
      rewardList,
      totalRewardUSD,
      profileEmode,
      rawRewards,
    }) => ({
      id: address,
      name,
      displayName,
      summary: {
        apy: totalApyPct,
        isApyPositive: totalApyPct > 0,
        depositedValueUSD: `$${totalDepositedUSD
          .toNumber()
          .toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`,
        borrowedValueUSD: `$${totalLoanUSD
          .toNumber()
          .toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`,
        availableMarginUSD: `$${
          availableMarginUSD.lt(0)
            ? 0
            : availableMarginUSD.toNumber().toLocaleString(undefined, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })
        }`,
        networth: `$${networth.toFixed(2)}`,
        borrowingPowerPct: borrowPower,
        loans: loanList,
        deposits: depositList,
        rewards: rewardList,
        rewardValueUSD: `$${totalRewardUSD.toFixed(2)}`,
        riskFactorPct,
        emodeId: profileEmode?.emodeId,
      },
      claimRewards: (reserveCoinAddress: string) =>
        claimRewards(name, reserveCoinAddress, rawRewards),
    }),
  );

  const { unhealthyProfiles, loading: profileListLoading } =
    useUnhealthyProfiles();

  const borrowRebates = useUserBorrowRebate();
  return {
    reserves: reserveWithProfile,
    reserveLoading: loading,
    reserveEmpty: false,
    unhealthyProfiles,
    obligationEmpty: !loading && unhealthyProfiles.length === 0,
    obligationLoading:
      unhealthyProfiles.length === 0 && profileListLoading,
    triggerRefreshObligation: noop,
    profile: {
      initProfile: initMainProfile,
      addProfile,
      changeProfile,
      exitEmode: () => exitEmode(),
      initialized: (profileList.length ?? 0) > 0,
      list: profiles,
      currentProfile: profiles.find(p => p.id === currentProfileKey),
      currentProfileId: currentProfileKey,
      currentProfileLoading: profileLoading,
    },
    summary,
    faucets,
    env,
    wallet: walletCtx,
    borrowRebates,
  };
};

export const [useAptosLendingApp, getAptosLendingApp] = createGlobalStore(
  useAptosLendingAppImpl,
);
